import React, { useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Typography,
    Box,
    Alert,
    FormControlLabel,
    Checkbox,
    Link
} from '@mui/material';
import EmailIcon from '@mui/icons-material/Email';

const EmailCollectionModal = ({ open, onClose, onSubmit, title, description }) => {
    const [name, setName] = useState('');
    const [email, setEmail] = useState('');
    const [error, setError] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [gdprConsent, setGdprConsent] = useState(false);
    const [marketingConsent, setMarketingConsent] = useState(false);

    const validateEmail = (email) => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(email);
    };

    const handleSubmit = async () => {
        setError('');

        if (!name.trim()) {
            setError('Proszę podać imię');
            return;
        }

        if (!email.trim()) {
            setError('Proszę podać adres email');
            return;
        }

        if (!validateEmail(email)) {
            setError('Proszę podać prawidłowy adres email');
            return;
        }

        if (!gdprConsent) {
            setError('Musisz zaakceptować przetwarzanie danych osobowych');
            return;
        }

        if (!marketingConsent) {
            setError('Musisz wyrazić zgodę na kontakt marketingowy');
            return;
        }

        setIsSubmitting(true);

        try {
            await onSubmit({ name: name.trim(), email: email.trim() });
            setName('');
            setEmail('');
            setGdprConsent(false);
            setMarketingConsent(false);
            onClose();
        } catch (err) {
            setError('Wystąpił błąd. Spróbuj ponownie.');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleClose = () => {
        if (!isSubmitting) {
            setName('');
            setEmail('');
            setError('');
            setGdprConsent(false);
            setMarketingConsent(false);
            onClose();
        }
    };

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="sm"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: 3,
                    p: 2
                }
            }}
        >
            <DialogTitle sx={{ textAlign: 'center', pb: 1 }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                    <EmailIcon
                        sx={{
                            fontSize: 50,
                            color: 'primary.main',
                            mb: 1
                        }}
                    />
                    <Typography variant="h5" component="h2" sx={{ fontWeight: 'bold', color: 'grey.800' }}>
                        {title || 'Podaj swoje dane'}
                    </Typography>
                </Box>
            </DialogTitle>

            <DialogContent sx={{ py: 3 }}>
                <Typography variant="body1" sx={{ mb: 3, color: 'grey.700', textAlign: 'center' }}>
                    {description || 'Podaj swoje imię i adres email.'}
                </Typography>

                <TextField
                    fullWidth
                    label="Imię"
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Twoje imię"
                    disabled={isSubmitting}
                    sx={{
                        mb: 2,
                        '& .MuiOutlinedInput-root': {
                            borderRadius: 2
                        }
                    }}
                />

                <TextField
                    fullWidth
                    label="Adres email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="twoj@email.com"
                    disabled={isSubmitting}
                    onKeyPress={(e) => {
                        if (e.key === 'Enter') {
                            handleSubmit();
                        }
                    }}
                    sx={{
                        mb: 3,
                        '& .MuiOutlinedInput-root': {
                            borderRadius: 2
                        }
                    }}
                />

                <Box sx={{ mb: 2 }}>
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={gdprConsent}
                                onChange={(e) => setGdprConsent(e.target.checked)}
                                disabled={isSubmitting}
                                color="primary"
                            />
                        }
                        label={
                            <Typography variant="body2" sx={{ color: 'grey.700' }}>
                                Akceptuję{' '}
                                <Link
                                    href="/assets/KLAUZULA INFORMACYJNA GS.pdf"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    sx={{ color: 'primary.main', textDecoration: 'underline' }}
                                >
                                    Klauzulę informacyjną
                                </Link>
                                {' '}i{' '}
                                <Link
                                    href="/assets/Polityka prywatności.pdf"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    sx={{ color: 'primary.main', textDecoration: 'underline' }}
                                >
                                    Politykę prywatności
                                </Link>
                                {' '}oraz wyrażam zgodę na przetwarzanie moich danych osobowych. *
                            </Typography>
                        }
                        sx={{ alignItems: 'flex-start', mb: 1.5 }}
                    />
                </Box>

                <Box sx={{ mb: 2 }}>
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={marketingConsent}
                                onChange={(e) => setMarketingConsent(e.target.checked)}
                                disabled={isSubmitting}
                                color="primary"
                            />
                        }
                        label={
                            <Typography variant="body2" sx={{ color: 'grey.700' }}>
                                Wyrażam zgodę na{' '}
                                <Link
                                    href="/assets/Zgoda na marketing bezpośredni i przesyłanie informacji handlowych GS.pdf"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    sx={{ color: 'primary.main', textDecoration: 'underline' }}
                                >
                                    kontakt w celach marketingowych
                                </Link>
                                {' '}oraz{' '}
                                <Link
                                    href="/assets/Zgoda na otrzymanie oferty handlowej opracowana w oparciu o Prawo Komunikacji Elektronicznej  wraz z klauzulą informacyjną GS.pdf"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    sx={{ color: 'primary.main', textDecoration: 'underline' }}
                                >
                                    otrzymywanie informacji handlowych
                                </Link>
                                {' '}drogą elektroniczną. *
                            </Typography>
                        }
                        sx={{ alignItems: 'flex-start' }}
                    />
                </Box>

                {error && (
                    <Alert severity="error" sx={{ mt: 2 }}>
                        {error}
                    </Alert>
                )}
            </DialogContent>

            <DialogActions sx={{ justifyContent: 'space-between', px: 3, pb: 2 }}>
                <Button
                    variant="outlined"
                    onClick={handleClose}
                    disabled={isSubmitting}
                >
                    Anuluj
                </Button>
                <Button
                    variant="contained"
                    onClick={handleSubmit}
                    disabled={isSubmitting || !email.trim() || !name.trim() || !gdprConsent || !marketingConsent}
                    sx={{
                        background: 'linear-gradient(90deg, #c79a5f, #9b7b5e)',
                        px: 4,
                        py: 1.5,
                        borderRadius: 2,
                        fontWeight: 'bold',
                        '&:hover': {
                            background: 'linear-gradient(90deg, #c79a5f, #9b7b5e)',
                            transform: 'scale(1.05)'
                        },
                        '&:disabled': {
                            background: 'grey.400',
                            transform: 'none'
                        }
                    }}
                >
                    {isSubmitting ? 'Wysyłanie...' : 'Wyślij'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default EmailCollectionModal;
